import React from "react";
import { MapPin, Briefcase, Clock, GraduationCap, Wallet } from "@phosphor-icons/react";
import SEO from "@/components/SEO";
import PageHero from "@/components/PageHero";
import { JOBS } from "@/lib/site";

const PERKS = [
  { icon: Wallet, title: "Competitive Wages", body: "Above-market starting wages with shift differentials for nights and weekends." },
  { icon: GraduationCap, title: "Paid Training", body: "OSHA, bloodborne pathogens, and equipment certifications — paid time, no cost to you." },
  { icon: Briefcase, title: "Career Ladder", body: "Porter → Lead → Site Supervisor → Account Manager. We promote from within." },
];

export default function Careers() {
  return (
    <>
      <SEO title="Careers" description="Join Empire Serv. Open roles for commercial janitorial, general maintenance, and floor care specialists across the Carolinas." path="/careers" />
      <PageHero
        eyebrow="Careers"
        title="Build a career, not just a job."
        subtitle="Open roles across the Carolinas — with paid training and a path from technician to account manager."
        image="https://images.pexels.com/photos/8273515/pexels-photo-8273515.jpeg"
        testId="careers-hero"
      />

      <section className="es-section bg-white">
        <div className="es-container">
          <div className="es-eyebrow">Why Empire</div>
          <h2 className="es-h2 text-[var(--es-navy)] mt-3 max-w-3xl">A workplace that pays you back.</h2>
          <div className="mt-12 grid md:grid-cols-3 gap-6">
            {PERKS.map((p, i) => (
              <div key={p.title} data-testid={`career-perk-${i}`} className="es-card p-7">
                <p.icon size={26} weight="duotone" className="text-[var(--es-accent)]" />
                <h3 className="mt-4 text-lg font-bold text-[var(--es-navy)]" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>{p.title}</h3>
                <p className="mt-2 text-sm text-[var(--es-text-muted)] leading-relaxed">{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="es-section bg-[var(--es-bg)]">
        <div className="es-container">
          <div className="es-eyebrow">Open Positions</div>
          <h2 className="es-h2 text-[var(--es-navy)] mt-3">Now hiring across the Triad.</h2>
          <div className="mt-10 grid md:grid-cols-3 gap-6">
            {JOBS.map((j, i) => (
              <div key={i} data-testid={`career-job-${i}`} className="es-card p-7 flex flex-col">
                <div className="w-11 h-11 bg-[var(--es-navy)] text-[var(--es-accent)] rounded-sm flex items-center justify-center mb-5">
                  <Briefcase size={20} weight="duotone" />
                </div>
                <h3 className="text-lg font-bold text-[var(--es-navy)] leading-snug" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>{j.title}</h3>
                <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-[var(--es-text-muted)]">
                  <span className="flex items-center gap-1"><MapPin size={13} weight="fill" />{j.location}</span>
                  <span className="flex items-center gap-1"><Briefcase size={13} weight="fill" />{j.type}</span>
                  <span className="flex items-center gap-1"><Clock size={13} weight="fill" />{j.shift}</span>
                </div>
                <p className="mt-4 text-sm text-[var(--es-text-muted)] leading-relaxed flex-1">{j.description}</p>
                <a href="#apply" className="mt-5 es-btn-outline text-sm !py-2 self-start" data-testid={`career-apply-${i}`}>Apply</a>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="apply" className="es-section bg-white">
        <div className="es-container max-w-4xl">
          <div className="es-eyebrow">Apply</div>
          <h2 className="es-h2 text-[var(--es-navy)] mt-3">Submit your application.</h2>
          <p className="es-lede mt-4">Complete the application form below. Our HR team will respond within three business days.</p>

          <div className="mt-10 bg-white border border-[var(--es-border)] rounded-sm overflow-hidden" data-testid="career-application-embed">
            <iframe
              src="https://api.leadconnectorhq.com/widget/form/lai7r2oW1LXFAY1iNRZF"
              title="Empire Serv Career Application"
              style={{ width: "100%", height: "600px", border: "none" }}
              data-testid="career-application-iframe"
            />
          </div>
          <p className="mt-6 text-xs text-[var(--es-text-muted)]">Empire Serv is an Equal Opportunity Employer. We do not discriminate on the basis of race, color, religion, gender, sexual orientation, national origin, age, disability, or veteran status.</p>
        </div>
      </section>
    </>
  );
}
